<script setup>
// core
import { ref } from 'vue';

// components
import ClockIcon from "@/components/Icons/Table/ClockIcon.vue";
import TrashIcon from "@/components/Icons/Table/TrashIcon.vue";

// helpers
import { conjugationMonth } from "@/helpers/conjugation";

const props = defineProps({
    data: {
        type: Array,
        required: true,
        default: () => []
    }
})

const emit = defineEmits(["delete", "viewHistory"]);

const expandedItems = ref(new Set());

const toggleItem = (id) => {
    if (expandedItems.value.has(id)) {
        expandedItems.value.delete(id);
    } else {
        expandedItems.value.add(id);
    }
}

const getDate = (date) => {
    if (!date) return;
    const dateObj = new Date(date);
    return dateObj.toISOString().slice(0, 10);
}
</script>

<template>
    <ul class="subs-mobile">
        <li v-for="sub in data" :key="sub.subscription_id" class="subs-mobile__item">
            <div class="subs-mobile__header" @click="toggleItem(sub.subscription_id)">
                <div class="subs-mobile__main-info">
                    <div class="subs-mobile__amount">${{ sub.amount }}</div>
                    <div class="subs-mobile__status" :class="sub.status">
                        {{ $t(`page.subscriptions.status.${sub.status}`) }}
                    </div>
                </div>
                <div class="subs-mobile__arrow" :class="{ 'expanded': expandedItems.has(sub.subscription_id) }">
                    <i class="bi bi-chevron-down"></i>
                </div>
            </div>
            <transition name="slide">
                <div v-if="expandedItems.has(sub.subscription_id)" class="subs-mobile__details">
                    <div class="subs-mobile__detail-item">
                        <span class="label">{{ $t('page.subscriptions.table.headers.1') }}:</span>
                        <span class="value">{{ conjugationMonth(Number(sub.period), $t) }}</span>
                    </div>
                    <div class="subs-mobile__detail-item">
                        <span class="label">{{ $t('page.subscriptions.table.headers.2') }}:</span>
                        <span class="value">{{ getDate(sub.start_date) }}</span>
                    </div>
                    <div class="subs-mobile__detail-item">
                        <span class="label">{{ $t('page.subscriptions.table.headers.3') }}:</span>
                        <span class="value">{{ getDate(sub.next_billing_date) }}</span>
                    </div>
                    <div class="subs-mobile__actions">
                        <ClockIcon @click="$emit('viewHistory', sub)"/>
                        <TrashIcon @click="$emit('delete', sub.subscription_id)"/>
                    </div>
                </div>
            </transition>
        </li>
    </ul>
</template>

<style scoped lang="scss">
.subs-mobile {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &__item {
        background-color: var(--block-color);
        border-radius: 11px;
        overflow: hidden;
    }

    &__header {
        padding: 16px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        cursor: pointer;
    }

    &__main-info {
        display: flex;
        gap: 16px;
        align-items: center;
    }

    &__amount {
        font-weight: 600;
        font-size: 18px;
    }

    &__date {
        color: var(--text-secondary);
    }

    &__status {
        &.active {
            color: var(--success-color);
        }

        &.cancelled {
            color: var(--danger-color);
        }

        &.suspended {
            color: var(--warning-color);
        }
    }

    &__arrow {
        transition: transform 0.3s ease;
        font-size: 12px;
        color: var(--text-secondary);

        &.expanded {
            transform: rotate(180deg);
        }
    }

    &__details {
        padding: 16px;
        border-top: 1px solid var(--border-color);
        background-color: var(--block-color-secondary);
    }

    &__detail-item {
        display: flex;
        justify-content: space-between;
        margin-bottom: 8px;

        .label {
            color: var(--text-secondary);
        }
    }

    &__actions {
        display: flex;
        gap: 16px;
        margin-top: 16px;
        justify-content: flex-end;

        svg {
            cursor: pointer;
        }
    }
}

// Transition animations
.slide-enter-active,
.slide-leave-active {
    transition: all 0.3s ease-out;
    max-height: 300px;
    opacity: 1;
}

.slide-enter-from,
.slide-leave-to {
    max-height: 0;
    opacity: 0;
    padding-top: 0;
    padding-bottom: 0;
}
</style>
